import React, { ReactElement, ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import { Timestamp } from 'firebase/firestore';

import { NavigateOptions, useLocation } from 'react-router-dom';

import SvgIcon from '@mui/material/SvgIcon';

import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import EventIcon from '@mui/icons-material/Event';
import GolfCourseIcon from '@mui/icons-material/GolfCourse';
import GroupAddIcon from '@mui/icons-material/GroupAdd';
import HomeIcon from '@mui/icons-material/Home';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonIcon from '@mui/icons-material/Person';
import PersonOutlineIcon from '@mui/icons-material/PersonOutlined';
import SportsGolfIcon from '@mui/icons-material/SportsGolf';

import GratiIcon from '../components/common/GratiIcon';
/* import MahaloIcon from "./icons/MahaloIcon"; */

export interface NavigateToProps {
  to: string;
  options?: NavigateOptions;
}

export interface GratiNotice {
  title: string;
  message: string;
  displayAt: Timestamp;
  expiresAt: Timestamp;
  severity: 'error' | 'warning' | 'info' | 'success';
}

export interface ActionProps {
  icon?: typeof SvgIcon;
  name: string;
  handleClick: () => void;
  variant?: 'icon' | 'text' | 'outlined' | 'contained';
}

export interface PageContextProps {
  name: string;
  icon?: typeof SvgIcon;
  to: string;
  showNavRail?: boolean;
  showLogin: boolean;
  showLogout: boolean;
  showMembers: boolean;
  showAdminPro: boolean;
  showGod: boolean;
  showIfOneOrg: boolean;
  showIfOneLeague: boolean;
  actions?: ActionProps[];
  fab?: ActionProps;
}

const defaultPageContexts: PageContextProps[] = [
  { name: 'Home', icon: HomeIcon, to: '/', showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Events', icon: EventIcon, to: '/events', showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Members', icon: PersonIcon, to: '/members', showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Guests', icon: PersonOutlineIcon, to: '/guests', showNavRail: true, showLogin: true, showLogout: false, showMembers: false, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Requests', icon: GroupAddIcon, to: '/requests', showNavRail: true, showLogin: true, showLogout: false, showMembers: false, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Orgs', icon: SportsGolfIcon, to: '/orgs', showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: false, showIfOneLeague: false },
  { name: 'Leagues', icon: EmojiEventsIcon, to: '/leagues', showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: false },
  { name: 'Courses', icon: GolfCourseIcon, to: '/courses', showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Docs', icon: LibraryBooksIcon, to: '/handbooks', showNavRail: true, showLogin: true, showLogout: false, showMembers: true, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: 'Notices', icon: NotificationsIcon, to: '/notices', showNavRail: true, showLogin: true, showLogout: false, showMembers: false, showAdminPro: true, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: "", to: "/course", showLogin: false, showLogout: false, showMembers: true, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: "", to: "/event", showLogin: false, showLogout: false, showMembers: true, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: "", to: "/org", showLogin: false, showLogout: false, showMembers: true, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: "", to: "/league", showLogin: false, showLogout: false, showMembers: true, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
  { name: "", to: "/profile", showLogin: false, showLogout: false, showMembers: true, showAdminPro: false, showGod: true, showIfOneOrg: true, showIfOneLeague: true },
];

export interface AppProviderProps {
  children?: ReactNode
}

export interface AppContextModel {
  actions: ActionProps[];
  appTitle: string;
  getPageTitle: () => string;
  isHomePage: boolean;
  notices: GratiNotice[];
  pageIcon: typeof SvgIcon;
  pageContexts: PageContextProps[];
  previousLocation: string | null;
  setPageName: (name: string | null) => void;
  updateActions: (actions: ActionProps[]) => void;
  godActions?: ReactElement | null;
  updateGodActions: (godActions: ReactElement | null) => void;
  isSaving: boolean;
  setIsSaving: (isSaving: boolean) => void;
}

export const AppContext = React.createContext<AppContextModel>(
  {} as AppContextModel,
);

export function useApp(): AppContextModel {
  return useContext(AppContext)
}

export const AppProvider = ({ children }: AppProviderProps): ReactElement => {
  const location = useLocation();
  const appTitle = "Gratigolf";
  const pageContexts = defaultPageContexts;
  const [notices, setNotices] = useState<GratiNotice[]>([]);
  const [actions, setActions] = useState<ActionProps[]>([]);
  const [isHomePage, setIsHomePage] = useState<boolean>(location.pathname === "/");
  const [pageIcon, setPageIcon] = useState<typeof SvgIcon>(GratiIcon);
  const [pageName, setPageName] = useState<string | null>("Home");
  const [previousLocation, setPreviousLocation] = useState<string | null>(null);
  const [godActions, setGodActions] = useState<ReactElement | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const prevLocationRef = useRef<string | null>(null);

  useEffect(() => {
    const urlSegments = location.pathname.split("/");
    const urlPath = urlSegments[0] + "/" + urlSegments[1];
    const pageIcon = defaultPageContexts.find((page) => page.to === urlPath)?.icon || GratiIcon;
    setPageIcon(pageIcon === HomeIcon ? GratiIcon : pageIcon);
    setIsHomePage(location.pathname === "/");
    prevLocationRef.current = previousLocation;
    setPreviousLocation(location.pathname);
  }, [location.pathname, previousLocation]);

  const getPageTitle = (): string => {
    if (isHomePage) {
      return appTitle;
    } else {
      const urlSegments = location.pathname.split("/");
      const urlPath = urlSegments[0] + "/" + urlSegments[1];
      const pageTitle = defaultPageContexts.find((page) => page.to === urlPath)?.name || "";
      if (pageTitle === "") {
        return pageName ? pageName : '';
      } else {
        return pageTitle;
      }

    }
  }

  const updateActions = useCallback((actions: ActionProps[]): void => {
    setActions(actions);
  }, []);

  const updateGodActions = useCallback((godActions: ReactElement | null): void => {
    setGodActions(godActions);
  }, []);

  const values = {
    actions,
    appTitle, 
    getPageTitle,
    godActions,
    isHomePage,
    isSaving,
    notices,
    pageIcon,
    pageContexts,
    previousLocation: prevLocationRef.current,
    setActions,
    setGodActions,
    setIsSaving,
    setNotices,
    setPageName,
    updateActions,
    updateGodActions,
  }
  return (
    <AppContext.Provider 
      value={values}
    >
      {children}
    </AppContext.Provider>
  );
}

//export const useUserContext = (): UserContextState => {
//  return useContext(UserStateContext)
//}